import Dialog from '../../ui/modal/Dialog';
import React, {useMemo, useState} from 'react';
import {handleError, handleSuccess} from '../../utils/Apollo';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import {DateTime} from 'luxon';
import {Event, EventsFeed, QueryEventsArgs} from '../../schema';
import UICheckbox from '../../ui/form/UICheckbox';
import UIListBox from '../../ui/form/UIListBox';
import cn from 'classnames';

type Props = {
  id: string
  eventId: string
  show: boolean
  setShowDialog: (close: boolean) => void
}

interface QueryFeed {
  events: EventsFeed
}

export default function TransferDlg({id, eventId, show, setShowDialog}: Props) {
  const [transferAttendee] = useMutation(transferMutation);
  const [newEventId, setNewEventId] = useState<string | undefined>(undefined);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [showError, setShowError] = useState(false);

  const startBeginsOn = useMemo(() => DateTime.utc().plus({minutes: 1}).toString(), []);

  const {data, loading, error} = useQuery<QueryFeed, Pick<QueryEventsArgs, "startBeginsOn">>(query, {
    variables: {
      startBeginsOn
    }
  });

  const onConfirmTransfer = () => {
    if (!newEventId) {
      setShowError(true);
      return;
    }
    transferAttendee({
      variables: {id, eventId: newEventId, sendWelcomeEmail}
    }).then(() => {
      handleSuccess(`Attendee has been transferred`);
      setShowDialog(false);
    }, (apolloError) => handleError(apolloError));
  }

  const handleEventSelection = (selectedEventId: string) => {
    setShowError(false);
    setNewEventId(selectedEventId);
  }

  return (
    <Dialog show={show} actionBtnTitle="Transfer" onClose={() => setShowDialog(false)}
            title={`Transfer attendee to`} onConfirm={() => onConfirmTransfer()} closeBtnTitle="Cancel">
      <>
        {loading && <div>Loading...</div>}
        {error && <div>{error.message}</div>}
        {data &&
          <>
            <UIListBox value={newEventId} onChange={(value) => handleEventSelection(value)}
                       emptyLabel="Select event"
                       options={data.events.events.filter((x) => x.id !== eventId).map((event: Event) => ({
                         id: event.id,
                         value: event.id,
                         label: `${event.title}, ${DateTime.fromISO(event.schedule.start).toLocaleString(DateTime.DATE_MED)}`,
                         selected: false
                       }))}
            />
            <span className={cn('text-red text-sm', {'hidden': !showError})}>Select an event for a transfer</span>
            <UICheckbox className="mt-2" id="sendWelcomeEmail" label="Send a welcome email to the attendee"
                        checked={sendWelcomeEmail}
                        onChange={() => setSendWelcomeEmail(!sendWelcomeEmail)}/>
          </>
        }
      </>
    </Dialog>
  )
}

const query = gql`
  query SelectEvents($startBeginsOn: DateTime!) {
    events: events(startBeginsOn: $startBeginsOn, orderBy: start_asc, count: -1) {
      events {
        id
        title
        schedule {
          start
        }
      }
    }
  }
`

const transferMutation = gql`
  mutation TransferMutation($id: ID!, $eventId: ID!, $sendWelcomeEmail: Boolean!) {
    transferAttendee(attendeeId: $id, eventId: $eventId, sendWelcomeEmail: $sendWelcomeEmail) {
      id
      permissions
      status
    }
  }
`;
